import React from 'react';
import './TextInput.css';

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  /** Non-empty string marks the field invalid and renders under the input. */
  error?: string | null;
  /** Content rendered inside the control, before the input (e.g. "$", icon). */
  prefix?: React.ReactNode;
  /** Content rendered after the input (e.g. unit, clear button). */
  suffix?: React.ReactNode;
  /** Marks suffix as interactive (enables pointer events). */
  suffixInteractive?: boolean;
}

let idCounter = 0;
const useGeneratedId = (prefix: string) => {
  const ref = React.useRef<string | null>(null);
  if (ref.current === null) {
    idCounter += 1;
    ref.current = `${prefix}-${idCounter}`;
  }
  return ref.current;
};

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    {
      label,
      hint,
      error,
      prefix,
      suffix,
      suffixInteractive,
      id,
      required,
      disabled,
      className,
      ...rest
    },
    ref
  ) {
    const generatedId = useGeneratedId('bso-input');
    const inputId = id ?? generatedId;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;

    const describedBy = [
      error ? errorId : null,
      hint ? hintId : null
    ]
      .filter(Boolean)
      .join(' ') || undefined;

    const classes = [
      'bso-input-field',
      error ? 'bso-input-field--invalid' : '',
      disabled ? 'bso-input-field--disabled' : '',
      className ?? ''
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={classes}>
        {label && (
          <label htmlFor={inputId} className="bso-input-field__label">
            {label}
            {required && (
              <span aria-hidden="true" className="bso-input-field__required-mark">*</span>
            )}
          </label>
        )}
        <div className="bso-input-field__control">
          {prefix && <span className="bso-input-field__affix">{prefix}</span>}
          <input
            {...rest}
            ref={ref}
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className="bso-input-field__input"
          />
          {suffix && (
            <span
              className={[
                'bso-input-field__affix',
                suffixInteractive ? 'bso-input-field__affix--interactive' : ''
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {suffix}
            </span>
          )}
        </div>
        {error ? (
          <span id={errorId} className="bso-input-field__error" role="alert">
            {error}
          </span>
        ) : hint ? (
          <span id={hintId} className="bso-input-field__hint">
            {hint}
          </span>
        ) : null}
      </div>
    );
  }
);
